import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import { IForubusVaccineLogData } from "../../../../models/forubus-vaccine-log";

const FORUBUS_VACCINE_LOG_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ForubusVaccineLog/ForubusVaccineLogs";

interface IForubusVaccineDetailsModalProps {
  forubusVaccineData: IForubusVaccineLogData;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.forubusVaccine.details.";
const ForubusVaccineDetailsModal: React.FC<
  IForubusVaccineDetailsModalProps
> = ({ forubusVaccineData, onClose }) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLogData, setSelectedLogData] = useState<string>("");
  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await GlobalAxios.get(
          `${FORUBUS_VACCINE_LOG_ROUTE}?VaccineIdentifier=${forubusVaccineData.identifier}`,
        );

        if (response.data?.items) {
          setLogData(
            response.data.items.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
            })),
          );
        }
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [forubusVaccineData.identifier]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const columns: GridColDef[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "log_id") || "ID do Log",
      width: 80,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "success",
      headerName: t(translationPath + "status") || "Status",
      width: 100,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.value
                ? t(translationPath + "success") || "Sucesso"
                : t(translationPath + "error") || "Erro"
            }
            color={params.value ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "logData",
      headerName: t(translationPath + "log_data") || "Dados do Log",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const logData = params.value || "";
        const preview =
          logData.length > 150 ? logData.substring(0, 150) + "..." : logData;

        const handleShowFullLog = () => {
          setSelectedLogData(logData);
          setShowLogModal(true);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview}
            </Typography>
            {logData && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={handleShowFullLog}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes da Forubus Vaccine"} -{" "}
          {forubusVaccineData.identifier}
        </DialogTitle>
        <DialogContent
          sx={{ height: 500, width: "100%", marginTop: 0, overflow: "hidden" }}
        >
          <TableList
            columns={columns}
            rows={logData}
            fileName={`nfe-logs-${forubusVaccineData.identifier}`}
            showSelects={false}
            onRowSelectionModelChange={undefined}
            pageSizeOptions={[5, 10, 25, 50]}
            hideTitle={true}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={showLogModal}
        onClose={() => setShowLogModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "log_data") || "Dados do Log"}
        </DialogTitle>
        <DialogContent>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: (theme) => theme.palette.grey[100],
              padding: 2,
              borderRadius: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {selectedLogData}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogModal(false)}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForubusVaccineDetailsModal;
